import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChartBar, Clock, CheckCircle, Warning, Hourglass, ListPlus, Star } from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import useFieldConfigs from "../hooks/useFieldConfigs";
import UpdateTag from "../components/UpdateTag";
import { canCreateTickets, formatDate } from "../lib/utils";
import { Button } from "../components/ui/button";

const KPI_CONFIG = [
  { key: "open", label: "Open", icon: ListPlus, tone: "text-blue-600 bg-blue-50" },
  { key: "in_progress", label: "In Progress", icon: Clock, tone: "text-violet-600 bg-violet-50" },
  { key: "pending_client", label: "Pending Client", icon: Hourglass, tone: "text-amber-600 bg-amber-50" },
  { key: "completed", label: "Completed", icon: CheckCircle, tone: "text-emerald-600 bg-emerald-50" },
  { key: "rejected", label: "Rejected", icon: Warning, tone: "text-rose-600 bg-rose-50" },
];

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [ratings, setRatings] = useState(null);
  const [updates, setUpdates] = useState([]);
  const { fieldOn } = useFieldConfigs();
  const isManager = ["super_admin", "project_manager"].includes(user.role);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/tickets", { params: { page: 1, page_size: 6 } }).then((r) => setRecent(r.data.items));
    api.get("/updates").then((r) => setUpdates(r.data.updates.slice(0, 3))).catch(() => {});
    if (isManager) api.get("/reports/ratings").then((r) => setRatings(r.data)).catch(() => {});
  }, [isManager]);

  const typeData = stats
    ? Object.entries(stats.by_type).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }))
    : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Operations · {new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Here&apos;s a real-time snapshot of every request across your workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/tickets"><Button variant="outline" className="border-slate-300" data-testid="goto-tickets-button">View all tickets</Button></Link>
          {canCreateTickets(user) && (
            <Link to="/tickets/new"><Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dashboard-new-ticket-button">New ticket</Button></Link>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {KPI_CONFIG.map(({ key, label, icon: Icon, tone }) => (
          <div key={key} className="surface-card p-5 hover:shadow-card-hover transition-shadow" data-testid={`kpi-${key}`}>
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone}`}>
                <Icon size={20} weight="regular" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</div>
            </div>
            <div className="mt-4">
              <div className="font-display text-3xl font-bold text-slate-900 tabular-nums tracking-tight">{stats?.totals?.[key] ?? "—"}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Avg Resolution + Completion Ratio */}
      <div className={`grid grid-cols-1 md:grid-cols-3 ${isManager ? "lg:grid-cols-4" : ""} gap-3`}>
        <div className="surface-card p-6" data-testid="metric-avg-resolution">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Avg. resolution time</div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{stats?.avg_resolution_hours ?? 0}</span>
            <span className="text-sm text-slate-500">{stats?.hours_basis === "business" ? "business hrs" : "hours"}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {stats?.hours_basis === "business" ? "Business hours across resolved tickets." : "Across resolved tickets in workspace."}
          </div>
        </div>
        <div className="surface-card p-6" data-testid="metric-completion-ratio">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Completion ratio</div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{stats?.completion_ratio ?? 0}</span>
            <span className="text-sm text-slate-500">%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${stats?.completion_ratio ?? 0}%` }} />
          </div>
        </div>
        <div className="surface-card p-6" data-testid="metric-total-tickets">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Total tickets</div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{stats?.totals?.all ?? 0}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">All-time count in your scope.</div>
        </div>
        {isManager && (
          <div className="surface-card p-6" data-testid="metric-avg-rating">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Customer rating</div>
            {ratings?.summary?.count ? (
              <>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{ratings.summary.avg}</span>
                  <span className="text-sm text-slate-500">/ 5</span>
                </div>
                <div className="mt-2 flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={14} weight={i <= Math.round(ratings.summary.avg) ? "fill" : "regular"}
                      className={i <= Math.round(ratings.summary.avg) ? "text-amber-400" : "text-slate-300"} />
                  ))}
                  <span className="text-xs text-slate-500 ml-1.5">from {ratings.summary.count} rating{ratings.summary.count === 1 ? "" : "s"}</span>
                </div>
              </>
            ) : (
              <div className="mt-2 text-sm text-slate-400">No ratings yet</div>
            )}
          </div>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="surface-card p-5 lg:col-span-2" data-testid="chart-monthly">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Monthly volume</div>
              <h3 className="font-display text-base font-semibold text-slate-900 mt-1">Tickets created · Last 6 months</h3>
            </div>
            <ChartBar size={18} className="text-slate-300" />
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {fieldOn("category") && (
        <div className="surface-card p-5" data-testid="chart-categories">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Request distribution</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-1 mb-2">By category</h3>
          <div className="h-[260px]">
            {typeData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-slate-400">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        )}
      </div>

      {updates.length > 0 && (
        <div className="surface-card overflow-hidden" data-testid="latest-updates-card">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Product</div>
            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Latest updates</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {updates.map((u) => (
              <div key={u.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <UpdateTag tag={u.tag} />
                  <span className="text-sm font-medium text-slate-800 truncate">{u.title}</span>
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(u.published_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent tickets */}
      <div className="surface-card overflow-hidden" data-testid="recent-tickets-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Activity</div>
            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Recent tickets</h3>
          </div>
          <Link to="/tickets" className="text-xs text-blue-600 hover:underline font-medium">See all →</Link>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm table-tight" data-testid="recent-tickets-table">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
              <th>Ticket</th><th>Subject</th><th>Project</th><th>Status</th>{fieldOn("priority") && <th>Priority</th>}<th>Created</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 && (
              <tr><td colSpan={fieldOn("priority") ? 6 : 5} className="text-center text-slate-400 py-10">No tickets yet.</td></tr>
            )}
            {recent.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                <td>
                  <Link to={`/tickets/${t.id}`} className="font-mono text-blue-600 hover:underline" data-testid={`recent-ticket-${t.ticket_no}`}>{t.ticket_no}</Link>
                </td>
                <td className="font-medium text-slate-800 max-w-xs truncate">{t.subject}</td>
                <td className="text-slate-500">{t.project?.name || "—"}</td>
                <td><StatusBadge status={t.status} /></td>
                {fieldOn("priority") && <td><PriorityBadge priority={t.priority} /></td>}
                <td className="text-slate-500 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
